import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ErreurApi, EVENEMENT_SESSION_EXPIREE, appelApi, urlApi } from './api';
import { parametresVueGlobale } from './useVueGlobale';
import type { ChoixPeriode } from './useDashboardDirection';
import type { IndicateurSynthese } from './useSynthesesMensuelles';

/**
 * Rapports personnalisables de la direction (TF-480) : période, comparaison,
 * critères et filtres choisis. Indicateurs et tableaux arrivent prêts à
 * afficher (`domain/rapports.ts`, présentés comme la synthèse mensuelle) :
 * l'écran ne recompte rien.
 */

export type CritereRapport =
  'CONTRATS' | 'IMMERSIONS' | 'SIGNATURES' | 'MAINTIENS' | 'RUPTURES' | 'ABSENCES' | 'RESULTATS';

export const CRITERES_RAPPORT: readonly CritereRapport[] = [
  'CONTRATS',
  'IMMERSIONS',
  'SIGNATURES',
  'MAINTIENS',
  'RUPTURES',
  'ABSENCES',
  'RESULTATS',
];

export type ComparaisonRapport = 'PRECEDENTE' | 'LIBRE' | 'AUCUNE';

export interface CelluleRapport {
  valeur: number | null;
  texte: string;
  precedent: number | null;
  precedentTexte: string | null;
}

export interface LigneTableauRapport {
  cle: string;
  libelle: string;
  cellules: CelluleRapport[];
}

export interface TableauRapport {
  id: string;
  titre: string;
  entete: string;
  colonnes: { libelle: string }[];
  lignes: LigneTableauRapport[];
  ensemble: LigneTableauRapport | null;
}

export interface SectionRapport {
  critere: CritereRapport;
  titre: string;
  indicateurs: IndicateurSynthese[];
  tableaux: TableauRapport[];
  regle: string;
}

export interface FormateurOption {
  id: string;
  nom: string;
  sites: string[];
}

export interface ReponseRapport {
  calculeLe: string;
  periode: { choix: ChoixPeriode; du: string; au: string; arreteeAu: string; libelle: string };
  comparaison: { choix: ComparaisonRapport; du: string; au: string; libelle: string } | null;
  criteres: CritereRapport[];
  antennes: string[];
  formation: { code: string; titre: string } | null;
  formateur: FormateurOption | null;
  seuil: number;
  sections: SectionRapport[];
  vide: boolean;
  options: {
    periodes: { valeur: ChoixPeriode; libelle: string }[];
    comparaisons: { valeur: ComparaisonRapport; libelle: string }[];
    criteres: { valeur: CritereRapport; libelle: string }[];
    formations: { code: string; titre: string }[];
    formateurs: FormateurOption[];
  };
}

/** Le rapport demandé : ce que l'adresse de l'écran garde, l'antenne en plus. */
export interface DemandeRapport {
  periode: ChoixPeriode;
  du: string;
  au: string;
  comparaison: ComparaisonRapport;
  cdu: string;
  cau: string;
  criteres: CritereRapport[];
  formation: string;
  formateur: string;
}

export const DEMANDE_PAR_DEFAUT: DemandeRapport = {
  periode: 'MOIS',
  du: '',
  au: '',
  comparaison: 'PRECEDENTE',
  cdu: '',
  cau: '',
  criteres: [...CRITERES_RAPPORT],
  formation: '',
  formateur: '',
};

const CHOIX_PERIODE: readonly ChoixPeriode[] = ['MOIS', 'MOIS_PRECEDENT', 'TRIMESTRE', 'ANNEE', 'LIBRE'];
const COMPARAISONS: readonly ComparaisonRapport[] = ['PRECEDENTE', 'LIBRE', 'AUCUNE'];

function dansLaListe<T extends string>(liste: readonly T[], valeur: string | null, defaut: T): T {
  return liste.includes(valeur as T) ? (valeur as T) : defaut;
}

/** Ce qu'une période personnalisée incomplète ou à l'envers empêche de demander ; `null` sinon. */
export function refusDemande(demande: DemandeRapport): string | null {
  if (demande.periode === 'LIBRE' && (!demande.du || !demande.au))
    return 'Indiquez le premier et le dernier jour de la période.';
  if (demande.periode === 'LIBRE' && demande.du > demande.au)
    return 'Le premier jour de la période doit précéder le dernier.';
  if (demande.comparaison === 'LIBRE' && (!demande.cdu || !demande.cau)) {
    return 'Indiquez le premier et le dernier jour de la période de comparaison.';
  }
  if (demande.comparaison === 'LIBRE' && demande.cdu > demande.cau) {
    return 'Le premier jour de la période de comparaison doit précéder le dernier.';
  }
  if (demande.criteres.length === 0) return 'Choisissez au moins un critère.';
  return null;
}

/** La demande lue dans l'adresse de l'écran : un lien gardé redonne le même rapport. */
export function demandeDeLAdresse(parametres: URLSearchParams): DemandeRapport {
  const criteres = parametres.get('criteres');
  return {
    periode: dansLaListe(CHOIX_PERIODE, parametres.get('periode'), DEMANDE_PAR_DEFAUT.periode),
    du: parametres.get('du') ?? '',
    au: parametres.get('au') ?? '',
    comparaison: dansLaListe(COMPARAISONS, parametres.get('comparaison'), DEMANDE_PAR_DEFAUT.comparaison),
    cdu: parametres.get('cdu') ?? '',
    cau: parametres.get('cau') ?? '',
    criteres:
      criteres === null
        ? [...CRITERES_RAPPORT]
        : CRITERES_RAPPORT.filter((critere) => criteres.split(',').includes(critere)),
    formation: parametres.get('formation') ?? '',
    formateur: parametres.get('formateur') ?? '',
  };
}

/** L'adresse de la demande : seul ce qui diffère du rapport par défaut y est écrit. */
export function adresseDeLaDemande(demande: DemandeRapport): URLSearchParams {
  const tous = demande.criteres.length === CRITERES_RAPPORT.length;
  return new URLSearchParams(
    parametresVueGlobale({
      periode: demande.periode === DEMANDE_PAR_DEFAUT.periode ? undefined : demande.periode,
      du: demande.periode === 'LIBRE' ? demande.du : undefined,
      au: demande.periode === 'LIBRE' ? demande.au : undefined,
      comparaison: demande.comparaison === DEMANDE_PAR_DEFAUT.comparaison ? undefined : demande.comparaison,
      cdu: demande.comparaison === 'LIBRE' ? demande.cdu : undefined,
      cau: demande.comparaison === 'LIBRE' ? demande.cau : undefined,
      criteres: tous ? undefined : demande.criteres.join(','),
      formation: demande.formation,
      formateur: demande.formateur,
    }),
  );
}

function chaine(demande: DemandeRapport, site: string): string {
  const parametres = adresseDeLaDemande(demande);
  if (site && site !== 'TOUS') parametres.set('site', site);
  return parametres.toString();
}

export function useRapport(demande: DemandeRapport, site: string) {
  const parametres = chaine(demande, site);
  return useQuery({
    queryKey: ['rapports', parametres],
    queryFn: () => appelApi<ReponseRapport>(`/rapports?${parametres}`),
    placeholderData: keepPreviousData,
    enabled: refusDemande(demande) === null,
  });
}

/**
 * Exporte le rapport demandé en Excel ou en PDF. Passe par `fetch` : un refus
 * de l'API s'affiche à l'écran au lieu de s'enregistrer comme un faux fichier.
 */
export async function exporterRapport(
  format: 'xlsx' | 'pdf',
  demande: DemandeRapport,
  site: string,
): Promise<{ nomFichier: string }> {
  const reponse = await fetch(urlApi(`/rapports/export?${chaine(demande, site)}`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ format }),
  });
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as {
      erreur?: { code?: string; message?: string; details?: unknown };
    } | null;
    if (reponse.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_EXPIREE));
    throw new ErreurApi(
      reponse.status,
      erreur?.erreur?.code ?? 'ERREUR',
      erreur?.erreur?.message ?? 'L’export n’a pas pu être produit.',
      erreur?.erreur?.details,
    );
  }
  const nomFichier =
    /filename="([^"]+)"/.exec(reponse.headers.get('content-disposition') ?? '')?.[1] ?? `rapport.${format}`;
  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
  return { nomFichier };
}
